import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Beaker, Leaf, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { RelatedReading } from "@/components/RelatedReading";
import { References } from "@/components/References";
import { ClusterLinks } from "@/components/ClusterLinks";
import { AuthorityFlow } from "@/components/AuthorityFlow";
import { relatedArticles } from "@/lib/clusters";
import heroAsset from "@/assets/blog/irregular-periods.jpg.asset.json";
import { ClinicalProductPitch } from "@/components/ClinicalProductPitch";

const URL = "https://truwe.in/blog/irregular-periods-pcos-checklist";
const TITLE = "Irregular Periods & PCOS: A 7-Point Checklist Before You Panic (India, 2026) | TruWe";

export const Route = createFileRoute("/blog/irregular-periods-pcos-checklist")({
  component: Article,
  head: () =>
    buildBlogHead({
      slug: "irregular-periods-pcos-checklist",
      title: TITLE,
      description:
        "Irregular periods aren't always PCOS. A 7-point checklist bloodwork, ultrasound, thyroid, insulin, prolactin that separates PCOS from stress, thyroid and post-pill cycles.",
      ogTitle: "Irregular Periods & PCOS, 7-Point Checklist",
      ogDescription:
        "Before you panic-Google PCOS, run this 7-point checklist that most gynaecologists use.",
      headline: "Irregular Periods & PCOS: A 7-Point Checklist Before You Panic",
      breadcrumbName: "Irregular Periods & PCOS Checklist",
      datePublished: "2026-07-18",
      dateModified: "2026-07-25",
      ogImage: `https://truwe.in${heroAsset.url}`,
      faqs: [
        {
          q: "How irregular does a cycle have to be to worry?",
          a: "Cycles shorter than 21 days, longer than 35 days, or varying by more than 7-9 days month to month are worth investigating. So is fewer than 8 cycles in a year.",
        },
        {
          q: "Can stress alone cause missed periods?",
          a: "Yes. Severe stress, under-eating, over-training or rapid weight loss can suppress ovulation via the hypothalamus, this is called functional hypothalamic amenorrhoea and is not PCOS.",
        },
        {
          q: "Do I need every test on this checklist?",
          a: "Not always. Start with the essentials TSH, prolactin, fasting insulin/glucose, testosterone, LH, FSH and add ultrasound and AMH if the picture points to PCOS.",
        },
        {
          q: "How quickly can regular cycles return after treatment?",
          a: "With inositol, lifestyle changes and (if needed) medication, cycles typically start regularising within 3-6 months.",
        },
      ],
    }),
});

const CHECKS = [
  {
    name: "TSH + free T4",
    note: "Thyroid dysfunction is the most-missed cause of irregular cycles. A high TSH (>4) can look identical to PCOS symptomatically.",
  },
  {
    name: "Prolactin",
    note: "Elevated prolactin (from stress, medications, a pituitary microadenoma) suppresses ovulation. A single test rules it in or out.",
  },
  {
    name: "Fasting insulin + fasting glucose (HOMA-IR)",
    note: "The core PCOS driver. A fasting insulin >10 or HOMA-IR >2 points at insulin resistance, the target for inositol, berberine and lifestyle changes.",
  },
  {
    name: "Total & free testosterone, SHBG, DHEA-S",
    note: "Distinguishes PCOS from adrenal androgen excess. Free testosterone is the more useful number.",
  },
  {
    name: "LH : FSH ratio",
    note: "A ratio >2 on day 2-3 of the cycle points toward PCOS. Not diagnostic alone but a strong supporting signal.",
  },
  {
    name: "Pelvic ultrasound (transvaginal or abdominal)",
    note: "Looks for the characteristic 'string of pearls' ovarian morphology (12+ small follicles). Rotterdam criteria need 2 of 3: irregular cycles + androgen excess + ultrasound morphology.",
  },
  {
    name: "AMH (anti-Müllerian hormone)",
    note: "Very high AMH (>4.5) is common in PCOS. Useful for confirming, and for fertility planning.",
  },
] as const;

function Article() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            Clinical Library · Hormonal Health
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            Irregular periods & PCOS: a 7-point checklist before you panic
          </h1>
          <BlogByline date="2026-07-18" readingTime="9 min read" />
          <div className="mt-4">
            <ReadWithLlm title={TITLE} url={URL} kind="article" />
          </div>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            Two missed cycles and Instagram tells you it's PCOS. Sometimes it is -
            sometimes it's thyroid, prolactin, stress or a rebound from stopping the
            pill. Before you commit to a diagnosis, run this checklist. It's the same
            workup most Indian gynaecologists start with.
          </p>
        <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={heroAsset.url}
              alt="Irregular periods and PCOS, cycle tracking checklist"
              width={1200}
              height={630}
              loading="eager"
              className="h-auto w-full"
            />
          </figure>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            What counts as "irregular"
          </h2>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>Cycle shorter than 21 days or longer than 35 days.</li>
            <li>Cycle-to-cycle variation of more than 7-9 days.</li>
            <li>Fewer than 8 cycles a year (oligomenorrhoea).</li>
            <li>Missed periods for 3+ months (amenorrhoea).</li>
          </ul>
          <p className="mt-3 text-sm text-muted-foreground">
            A single off cycle after travel, illness or a stressful week is not a
            pattern.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            The 7-point workup
          </h2>
          <div className="mt-5 space-y-4">
            {CHECKS.map((row, i) => (
              <div key={row.name} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <Beaker className="h-4 w-4 text-brand" />
                  <h3 className="font-display text-lg font-semibold text-brand-deep">
                    {i + 1}. {row.name}
                  </h3>
                </div>
                <p className="mt-2 text-sm text-foreground/80">{row.note}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            Common non-PCOS causes worth ruling out
          </h2>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>Hypothyroidism</strong> TSH &gt; 4 with fatigue, cold hands,
              constipation. Easy to treat.
            </li>
            <li>
              <strong>High prolactin</strong> often stress, sometimes a pituitary
              adenoma. Milky discharge is a red flag.
            </li>
            <li>
              <strong>Functional hypothalamic amenorrhoea</strong> from
              under-eating, over-training or extreme stress. Cycles return when energy
              availability rises.
            </li>
            <li>
              <strong>Post-pill amenorrhoea</strong> cycles can take 6+ months to
              normalise after stopping the pill. Not always PCOS.
            </li>
            <li>
              <strong>Premature ovarian insufficiency</strong> hot flashes and
              missed periods before 40. Needs an FSH + estradiol test.
            </li>
          </ul>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            If the checklist points to PCOS
          </h2>
          <p className="mt-3 text-base text-foreground/85">
            The evidence-based first-line stack is <strong>40:1
            myo:D-chiro-inositol + strength training + protein-forward
            meals</strong>. Add berberine and active folate for insulin-resistant PCOS,
            and vitamin D3 to sufficiency. Cycles typically start regularising in 3-6
            months. Read the deep-dive linked below.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-14 rounded-3xl border border-brand/15 bg-gradient-to-br from-brand/5 via-background to-brand-deep/5 p-8 md:p-10">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand">
              <Sparkles className="h-4 w-4" /> The TruWe formulation
            </div>
            <h2 className="mt-3 font-display text-2xl font-bold text-brand-deep md:text-3xl">
              Ova Balance, the first-line stack in one daily sachet
            </h2>
            <p className="mt-3 text-base text-foreground/85">
              If your workup points to PCOS, Ova Balance combines the exact
              first-line ingredients: <strong>40:1 myo:D-chiro-inositol, berberine HCl
              and Quatrefolic® L-5-MTHF</strong> at research-backed doses.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {[
                { icon: Leaf, label: "Gold-standard 40:1 ratio" },
                { icon: ShieldCheck, label: "Third-party tested" },
                { icon: Beaker, label: "Research-backed doses" },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 rounded-xl bg-background/70 px-3 py-3 text-sm text-foreground/85 ring-1 ring-border"
                >
                  <Icon className="h-4 w-4 text-brand" />
                  {label}
                </div>
              ))}
            </div>
            <Link
              to="/products/$handle"
              params={{ handle: "ova-balance" }}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-luxury transition hover:bg-brand-deep"
            >
              Ova Balance — 40:1 inositol for PCOS <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <ClinicalProductPitch cluster="hormonal" />
          <ClusterLinks cluster="hormonal" />
          <AuthorityFlow cluster="hormonal" />
          <References slug="irregular-periods-pcos-checklist" />
          <RelatedReading articles={relatedArticles("irregular-periods-pcos-checklist")} />
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-12 text-xs text-muted-foreground">
            Educational only. This checklist is a starting point, a full diagnosis and
            treatment plan should come from your gynaecologist or endocrinologist.
          </p>
        </Reveal>
      </div>
    </article>
  );
}
